import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Sparkles, AudioLines } from "lucide-react";
import Particles from "@/components/Particles";
import HoloDashboard from "@/components/HoloDashboard";
import { EASE } from "@/components/Reveal";

const LINES = ["The Future AI Workforce", "For Modern Businesses"];

export default function Hero() {
  const ref = useRef(null);
  const [conciergeQuery, setConciergeQuery] = useState("");
  const askConcierge = () => {
    const q = conciergeQuery.trim();
    if (!q) return;
    window.dispatchEvent(new CustomEvent("open-concierge", { detail: { message: q } }));
    setConciergeQuery("");
  };
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yDash = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-[72px]"
    >
      <div className="grid-lines absolute inset-0 [mask-image:radial-gradient(ellipse_75%_65%_at_50%_40%,black,transparent)]" />
      <Particles count={90} />
      <div className="beam" style={{ animationDelay: "0s" }} />
      <div className="beam" style={{ animationDelay: "4.5s", width: "140px" }} />
      <div className="beam" style={{ animationDelay: "8s", width: "300px" }} />
      <img
        src="/assets/emblem.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/2 w-[720px] max-w-none -translate-y-1/2 object-contain opacity-[0.07] animate-spin-slower"
      />
      <div className="hero-glow absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.09),transparent_65%)] blur-3xl" />
      <div className="hero-glow absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(74,222,128,0.05),transparent_65%)] blur-3xl" />
      {/* cinematic gold light sweep drifting slowly behind the headline */}
      <div className="hero-sweep" aria-hidden />
      <div className="hero-breathe" aria-hidden />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 py-24 lg:grid-cols-[1.05fr_0.95fr] lg:px-10">
        <motion.div style={{ y: yText, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.4, ease: EASE }}
            className="relative mb-10 inline-block"
          >
            <div className="absolute -inset-14 rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.4),transparent_70%)] blur-3xl" />
            <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.3),transparent_65%)] blur-xl" />
            <div className="absolute -inset-4 rounded-full border border-dashed border-gold/25 animate-spin-slower" />
            <img
              src="/assets/emblem.png"
              alt="Alkyrvis glowing emblem"
              className="animate-float relative h-28 w-28 object-contain drop-shadow-[0_0_55px_rgba(216,192,138,0.85)] md:h-36 md:w-36"
              data-testid="hero-emblem"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.45em] text-gold"
            data-testid="hero-label"
          >
            Alkyrvis — The AI Operating System for Business
          </motion.p>

          <h1
            className="font-display text-5xl font-light leading-[1.04] tracking-tighter text-[#F5F5F5] sm:text-6xl lg:text-7xl"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 1.15,
                    delay: 0.5 + i * 0.14,
                    ease: EASE,
                  }}
                >
                  {i === 0 ? (
                    line
                  ) : (
                    <>
                      For <span className="gold-text font-normal">Modern</span>{" "}
                      Businesses
                    </>
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg"
            data-testid="hero-subheading"
          >
            Build your own AI workforce. Finance. Sales. Marketing. HR.
            Operations. Customer Support. All working together, powered by
            Alkyrvis.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#start"
              data-testid="hero-cta-start"
              className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#050505] transition-[box-shadow,transform,background-color] duration-300 hover:scale-[1.04] hover:bg-gold-bright hover:shadow-[0_0_45px_rgba(216,192,138,0.5)] active:scale-95"
            >
              Start Building
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <button
              type="button"
              data-testid="hero-cta-demo"
              onClick={() => window.dispatchEvent(new CustomEvent("open-demo-modal"))}
              className="inline-flex items-center gap-2.5 rounded-full border border-gold/40 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-[background-color,box-shadow,transform] duration-300 hover:scale-[1.04] hover:bg-gold/10 hover:shadow-[0_0_30px_rgba(216,192,138,0.25)] active:scale-95"
            >
              Book A Demo
            </button>
            <a
              href="#demo"
              data-testid="hero-cta-watch"
              className="group inline-flex items-center gap-2.5 px-3 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 group-hover:border-gold/50 group-hover:bg-gold/10">
                <Play className="h-3.5 w-3.5 fill-current" />
              </span>
              Watch The Future
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.45, ease: EASE }}
            className="mt-6 flex max-w-md items-center gap-2.5 rounded-full border border-white/10 bg-black/50 px-5 py-1 transition-[border-color,box-shadow] duration-300 focus-within:border-gold/50 focus-within:shadow-[0_0_25px_rgba(216,192,138,0.15)]"
          >
            <Sparkles className="h-4 w-4 shrink-0 text-gold" />
            <input
              value={conciergeQuery}
              onChange={(e) => setConciergeQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askConcierge()}
              placeholder="Ask Alkyrvis what you need..."
              data-testid="hero-concierge-input"
              className="w-full bg-transparent py-2.5 font-mono text-xs text-[#F5F5F5] outline-none placeholder:text-white/25"
            />
            <button
              type="button"
              data-testid="hero-concierge-submit"
              onClick={askConcierge}
              aria-label="Ask the Alkyrvis Concierge"
              className="text-gold transition-[color,transform] duration-300 hover:scale-110 hover:text-gold-bright"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.button
            type="button"
            data-testid="hero-voice-orb"
            onClick={() => window.dispatchEvent(new CustomEvent("open-voice-orb"))}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6, ease: EASE }}
            className="group mt-7 flex max-w-md items-center gap-5 rounded-2xl border border-gold/25 bg-gold/[0.04] px-6 py-4 text-left backdrop-blur-xl transition-[border-color,box-shadow,transform] duration-300 hover:scale-[1.02] hover:border-gold/50 hover:shadow-[0_0_50px_rgba(216,192,138,0.25)] active:scale-[0.98]"
          >
            <span className="relative flex h-16 w-16 shrink-0 items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.5),transparent_70%)] blur-lg" />
              <motion.span
                className="absolute inset-0 rounded-full border border-gold/50"
                animate={{ scale: [1, 1.55], opacity: [0.8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
              />
              <motion.span
                className="absolute inset-0 rounded-full border border-gold/30"
                animate={{ scale: [1, 1.55], opacity: [0.6, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.7 }}
              />
              <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[radial-gradient(circle_at_35%_30%,rgba(216,192,138,0.4),rgba(5,5,5,0.95)_65%)] shadow-[0_0_35px_rgba(216,192,138,0.45)] transition-shadow duration-300 group-hover:shadow-[0_0_55px_rgba(216,192,138,0.7)]">
                <img src="/assets/emblem.png" alt="" className="h-8 w-8 object-contain" />
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2.5">
                <span className="font-display text-base font-medium tracking-tight text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold-bright">
                  Talk to Alkyrvis
                </span>
                <span className="flex items-center gap-1.5 rounded-full border border-mint/30 bg-mint/10 px-2.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-mint">
                  <span className="status-dot" style={{ width: 4, height: 4 }} />
                  Live
                </span>
              </span>
              <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                Voice mode — tap, speak, it answers
              </span>
            </span>
            <AudioLines className="h-5 w-5 shrink-0 text-gold transition-transform duration-300 group-hover:scale-110" />
          </motion.button>
        </motion.div>

        <motion.div style={{ y: yDash }} className="relative">
          <HoloDashboard />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-12 w-7 items-start justify-center rounded-full border border-white/15 p-2">
          <motion.div
            animate={{ y: [0, 14, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-2 w-1 rounded-full bg-gold"
          />
        </div>
      </motion.div>
    </section>
  );
}
